import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Download,
  Trash2,
  RotateCcw,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.js";
import { CapacitorHttp } from "@capacitor/core";
import audioStorageService from "../../services/audioStorageService";
import LoopNudgeControls from "./LoopNudgeControls";

const AudioPlayer = ({
  audioUrl,
  audioFilename,
  audioSize,
  audioDuration,
  onDownload = null,
  onRemove = null,
  onReplace = null,
  showControls = true,
  compact = false,
  hideMenu = false,
  // Loop control callbacks - called when loop state changes
  onLoopStateChange = null,
  onLoopControlsReady = null,
  // WaveSurfer instance callback — called with ws on ready, null on destroy
  onWaveSurferReady = null,
}) => {
  // Create unique ID for this component instance
  const componentId = useRef(Math.random().toString(36).substr(2, 9));

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(audioDuration || 0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [menuPosition, setMenuPosition] = useState("bottom");

  // A-B Loop functionality state (setters used by toggleLoopMarkers callback)
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [loopStart, setLoopStart] = useState(null);
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [loopEnd, setLoopEnd] = useState(null);
  const [showLoopMarkers, setShowLoopMarkers] = useState(false);
  // draggingMarker removed - now handled by WaveSurfer regions
  const [markerTooltip, setMarkerTooltip] = useState(null); // { type: 'start'|'end', time: number, x: number }

  // WaveSurfer state
  const [waveSurfer, setWaveSurfer] = useState(null);
  const [regionsPlugin, setRegionsPlugin] = useState(null);
  const [waveformLoading, setWaveformLoading] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const currentRegionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Refs for proper cleanup (memory leak prevention)
  const waveSurferRef = useRef(null);
  const blobUrlRef = useRef(null);
  const regionEventHandlerRef = useRef(null);
  const onWsReadyRef = useRef(onWaveSurferReady);

  // Debug re-renders
  useEffect(() => {
    // console.log(`🔄 AudioPlayer [${componentId.current}] re-rendered:`, {
    //   showMenu,
    //   showVolumeSlider,
    //   hideMenu,
    //   compact,
    //   duration,
    //   currentTime,
    //   audioUrl: !!audioUrl
    // });
  });

  // Keep onWaveSurferReady ref in sync
  useEffect(() => {
    onWsReadyRef.current = onWaveSurferReady;
  }, [onWaveSurferReady]);

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const audioRef = useRef(null);
  const waveformRef = useRef(null);
  const waveformRefVertical = useRef(null);
  const dropdownRef = useRef(null);
  const volumeSliderRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Initialize WaveSurfer
  useEffect(() => {
    if (!audioUrl) return;

    // Track if the effect has been cleaned up (for async operations)
    let isCancelled = false;

    const initializeWaveSurfer = async () => {
      // Wait for next tick to ensure container is mounted
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if effect was cleaned up while waiting
      if (isCancelled) return;

      const containerRef = compact ? waveformRef : waveformRefVertical;
      if (!containerRef.current) {
        console.error("WaveSurfer container not found, compact:", compact);
        return;
      }

      console.log(
        "🎵 Initializing WaveSurfer, container found:",
        !!containerRef.current,
      );

      // Clean up existing instance using ref (ensures we get the current instance)
      if (waveSurferRef.current) {
        console.log("🧹 Destroying previous WaveSurfer instance");
        waveSurferRef.current.destroy();
        waveSurferRef.current = null;
        setWaveSurfer(null);
      }

      // Clean up any existing blob URL
      if (blobUrlRef.current) {
        console.log("🧹 Revoking previous blob URL");
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      setWaveformLoading(true);
      setError(null);

      try {
        // Create regions plugin
        const regions = RegionsPlugin.create();
        setRegionsPlugin(regions);

        // Create WaveSurfer instance
        const ws = WaveSurfer.create({
          container: containerRef.current,
          waveColor: "#7ab892",
          progressColor: "#c5a34e",
          cursorColor: "#c5a34e",
          barWidth: 3,
          barGap: 1,
          barRadius: 1,
          responsive: true,
          height: compact ? 16 : 24,
          normalize: true,
          plugins: [regions],
          mediaControls: false,
          interact: true,
          backend: "WebAudio",
          fillParent: true,
        });

        // Event listeners
        ws.on("ready", () => {
          if (isCancelled) return;
          const duration = ws.getDuration();
          setDuration(duration);
          setIsLoading(false);
          setWaveformLoading(false);
          console.log("✅ WaveSurfer ready, duration:", duration);
          if (containerRef.current) {
            console.log(
              "📊 Container dimensions:",
              containerRef.current.getBoundingClientRect(),
            );
          }
          // Notify parent that WaveSurfer instance is ready
          if (onWsReadyRef.current) onWsReadyRef.current(ws);
        });

        ws.on("loading", (percent) => {
          if (isCancelled) return;
          console.log("📊 Loading progress:", percent + "%");
          setWaveformLoading(percent < 100);
        });

        // Use timeupdate for current time tracking and loop logic
        ws.on("timeupdate", (time) => {
          if (isCancelled) return;
          setCurrentTime(time);
        });

        ws.on("play", () => {
          if (isCancelled) return;
          setIsPlaying(true);
        });
        ws.on("pause", () => {
          if (isCancelled) return;
          setIsPlaying(false);
        });
        ws.on("finish", () => {
          if (isCancelled) return;
          setIsPlaying(false);
        });

        ws.on("error", (error) => {
          if (isCancelled) return;
          setError("Failed to load audio file");
          setIsLoading(false);
          setWaveformLoading(false);
          console.error("❌ WaveSurfer error:", error);
        });

        // Load the audio - handle IndexedDB URLs and Supabase URLs on mobile
        console.log("🎵 Loading audio URL:", audioUrl);
        let playableUrl = audioUrl;
        let createdBlobUrl = null;

        // If it's an IndexedDB reference, convert to blob URL
        if (audioUrl.startsWith("indexeddb://")) {
          const songId = audioUrl.replace("indexeddb://", "");
          console.log(
            "🎵 Converting IndexedDB URL to blob URL for song:",
            songId,
          );
          playableUrl = await audioStorageService.getAudioBlobURL(songId);
          createdBlobUrl = playableUrl;
          console.log("✅ Got blob URL:", playableUrl);
        }
        // On mobile/Android, convert Supabase URLs to blob URLs to avoid CORS issues
        else if (audioUrl.includes("supabase.co")) {
          const isCapacitor = window.Capacitor !== undefined;
          const isNativePlatform =
            window.Capacitor?.isNativePlatform?.() || false;

          console.log(
            "📱 Supabase URL detected, isCapacitor:",
            isCapacitor,
            "isNative:",
            isNativePlatform,
          );
          console.log("🔍 Original URL:", audioUrl);

          try {
            // Use CapacitorHttp for native apps (bypasses CORS)
            if (isCapacitor && isNativePlatform) {
              console.log(
                "🔌 Using CapacitorHttp to fetch audio (native platform)",
              );

              // CapacitorHttp.get doesn't support arraybuffer responseType directly
              // Use request method with blob handling
              const httpResponse = await CapacitorHttp.request({
                url: audioUrl,
                method: "GET",
                responseType: "blob",
              });

              console.log(
                "📥 CapacitorHttp response status:",
                httpResponse.status,
              );
              if (httpResponse.status !== 200) {
                throw new Error(`HTTP ${httpResponse.status}`);
              }

              // CapacitorHttp returns data as base64 for blob responseType
              // Convert base64 to blob
              if (httpResponse.data) {
                console.log("📦 Response data type:", typeof httpResponse.data);

                let blob;
                if (typeof httpResponse.data === "string") {
                  // Base64 string - convert to blob
                  const byteCharacters = atob(httpResponse.data);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  blob = new Blob([byteArray], { type: "audio/mpeg" });
                } else {
                  // Already blob-like
                  blob = new Blob([httpResponse.data], { type: "audio/mpeg" });
                }

                console.log("📦 Created blob, size:", blob.size);
                playableUrl = URL.createObjectURL(blob);
                createdBlobUrl = playableUrl;
                console.log("✅ Converted to blob URL via CapacitorHttp");
              } else {
                throw new Error("No data in response");
              }
            } else {
              // Fallback to regular fetch for web browsers
              console.log("🌐 Using fetch to load audio");
              const response = await fetch(audioUrl, {
                mode: "cors",
                credentials: "omit",
                headers: {
                  Accept: "audio/*",
                },
              });
              console.log("📥 Fetch response status:", response.status);
              if (!response.ok) {
                const errorText = await response.text();
                console.error("❌ Fetch error response:", errorText);
                throw new Error(
                  `HTTP ${response.status}: ${response.statusText}`,
                );
              }
              const blob = await response.blob();
              console.log("📦 Blob type:", blob.type, "size:", blob.size);
              playableUrl = URL.createObjectURL(blob);
              createdBlobUrl = playableUrl;
              console.log(
                "✅ Converted to blob URL:",
                playableUrl.substring(0, 50) + "...",
              );
            }
          } catch (fetchError) {
            console.error(
              "❌ Failed to convert Supabase URL to blob:",
              fetchError,
            );
            console.error("❌ Error name:", fetchError.name);
            console.error("❌ Error message:", fetchError.message);
            console.error(
              "❌ Full error:",
              JSON.stringify(
                fetchError,
                Object.getOwnPropertyNames(fetchError),
              ),
            );
            throw new Error(
              `Failed to load audio from cloud storage: ${fetchError.message}`,
            );
          }
        }

        // Check if cancelled before loading
        if (isCancelled) {
          // Clean up the blob URL we just created if cancelled
          if (createdBlobUrl) {
            URL.revokeObjectURL(createdBlobUrl);
          }
          ws.destroy();
          return;
        }

        // Store the blob URL in ref for cleanup
        if (createdBlobUrl) {
          blobUrlRef.current = createdBlobUrl;
        }

        await ws.load(playableUrl);

        // Check if cancelled after loading
        if (isCancelled) {
          ws.destroy();
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
          }
          return;
        }

        // Store WaveSurfer instance in both state and ref
        waveSurferRef.current = ws;
        setWaveSurfer(ws);
      } catch (error) {
        if (isCancelled) return;
        console.error("❌ Failed to initialize WaveSurfer:", error);
        setError("Failed to initialize audio player");
        setIsLoading(false);
        setWaveformLoading(false);
      }
    };

    initializeWaveSurfer();

    // Cleanup function - runs on unmount or when dependencies change
    return () => {
      console.log("🧹 AudioPlayer cleanup triggered");
      isCancelled = true;

      // Clean up region event handler first
      if (currentRegionRef.current && regionEventHandlerRef.current) {
        try {
          currentRegionRef.current.un(
            "update-end",
            regionEventHandlerRef.current,
          );
        } catch (e) {
          // Region may already be destroyed
        }
        regionEventHandlerRef.current = null;
      }

      // Destroy WaveSurfer instance using ref (ensures we get the current instance)
      if (waveSurferRef.current) {
        console.log("🧹 Destroying WaveSurfer instance");
        waveSurferRef.current.destroy();
        waveSurferRef.current = null;
        // Notify parent that WaveSurfer is gone
        if (onWsReadyRef.current) onWsReadyRef.current(null);
      }

      // Revoke blob URL to prevent memory leak
      if (blobUrlRef.current) {
        console.log("🧹 Revoking blob URL on cleanup");
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, compact]);

  // Handle A-B looping logic with proper access to current state
  useEffect(() => {
    if (!waveSurfer || !showLoopMarkers || !currentRegion) return;

    const handleTimeUpdate = (time) => {
      const LOOP_TOLERANCE = 0.1;
      if (time >= currentRegion.end - LOOP_TOLERANCE) {
        console.log(
          "🔄 Loop triggered - jumping from",
          time.toFixed(3),
          "to",
          currentRegion.start.toFixed(3),
        );
        waveSurfer.seekTo(currentRegion.start / waveSurfer.getDuration());
      }
    };

    waveSurfer.on("timeupdate", handleTimeUpdate);

    return () => {
      waveSurfer.un("timeupdate", handleTimeUpdate);
    };
  }, [waveSurfer, showLoopMarkers, currentRegion]);

  // Handle click outside to close menu
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        console.log(
          `🔘 [${componentId.current}] Click outside detected, closing menu`,
        );
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  // Handle click outside to close volume slider
  useEffect(() => {
    if (!showVolumeSlider) return;

    const handleClickOutside = (e) => {
      if (
        volumeSliderRef.current &&
        !volumeSliderRef.current.contains(e.target)
      ) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showVolumeSlider]);

  // Calculate menu position when menu opens
  useEffect(() => {
    if (showMenu && menuButtonRef.current) {
      const buttonRect = menuButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const menuHeight = 120; // Approximate height of menu dropdown

      // Account for notepad height on mobile
      const isMobile = window.innerWidth <= 768;
      const notepadHeight = isMobile ? 50 : 0;
      const availableHeight = viewportHeight - notepadHeight;

      // If there's not enough space below, show above
      if (buttonRect.bottom + menuHeight > availableHeight) {
        setMenuPosition("top");
      } else {
        setMenuPosition("bottom");
      }
    }
  }, [showMenu]);

  // Play/pause toggle
  const togglePlayPause = useCallback(async () => {
    if (!waveSurfer || isLoading || waveformLoading) return;

    try {
      if (isPlaying) {
        waveSurfer.pause();
      } else {
        waveSurfer.play();
      }
    } catch (error) {
      console.error("Play/pause error:", error);
      setError("Failed to play audio");
    }
  }, [waveSurfer, isPlaying, isLoading, waveformLoading]);

  // WaveSurfer handles seeking automatically through click events

  // Volume control
  const handleVolumeChange = useCallback(
    (newVolume) => {
      if (!waveSurfer) return;

      waveSurfer.setVolume(newVolume);
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    },
    [waveSurfer],
  );

  // Volume button toggle (mute/unmute or show/hide volume slider)
  const toggleVolume = useCallback(() => {
    if (hideMenu && compact) {
      // In notepad mode, toggle volume slider visibility
      setShowVolumeSlider(!showVolumeSlider);
    } else {
      // In normal mode, toggle mute
      if (!waveSurfer) return;

      if (isMuted) {
        waveSurfer.setVolume(volume || 0.5);
        setIsMuted(false);
      } else {
        waveSurfer.setVolume(0);
        setIsMuted(true);
      }
    }
  }, [waveSurfer, hideMenu, compact, showVolumeSlider, isMuted, volume]);

  // Mute toggle (for backwards compatibility)
  const toggleMute = useCallback(() => {
    if (!waveSurfer) return;

    if (isMuted) {
      waveSurfer.setVolume(volume || 0.5);
      setIsMuted(false);
    } else {
      waveSurfer.setVolume(0);
      setIsMuted(true);
    }
  }, [waveSurfer, isMuted, volume]);

  // Download handler
  const handleDownload = useCallback(async () => {
    if (!onDownload) return;

    try {
      await onDownload();
    } catch (error) {
      console.error("Download error:", error);
    }
  }, [onDownload]);

  // A-B Loop functionality with WaveSurfer regions
  const toggleLoopMarkers = useCallback(() => {
    if (!waveSurfer || !regionsPlugin) return;

    if (showLoopMarkers) {
      // Hide markers and disable loop functionality
      setShowLoopMarkers(false);
      setMarkerTooltip(null);

      // Clean up region event handler before removing region
      if (currentRegion && regionEventHandlerRef.current) {
        try {
          currentRegion.un("update-end", regionEventHandlerRef.current);
        } catch (e) {
          // Region may already be destroyed
        }
        regionEventHandlerRef.current = null;
      }

      if (currentRegion) {
        currentRegion.remove();
        setCurrentRegion(null);
        currentRegionRef.current = null;
      }
      setLoopStart(null);
      setLoopEnd(null);
    } else {
      // Show markers and set default positions
      setShowLoopMarkers(true);
      if (duration > 0) {
        const quarterDuration = duration * 0.25;
        const threeQuarterDuration = duration * 0.75;
        setLoopStart(quarterDuration);
        setLoopEnd(threeQuarterDuration);

        // Create region
        const region = regionsPlugin.addRegion({
          start: quarterDuration,
          end: threeQuarterDuration,
          color: "rgba(196, 90, 45, 0.25)",
          drag: true,
          resize: true,
          loop: true,
        });
        setCurrentRegion(region);
        currentRegionRef.current = region;

        // Handle region updates - store handler reference for cleanup
        const handleRegionUpdate = () => {
          setLoopStart(region.start);
          setLoopEnd(region.end);
          currentRegionRef.current = region;
          console.log("🔄 Region updated:", {
            start: region.start,
            end: region.end,
          });
        };
        regionEventHandlerRef.current = handleRegionUpdate;
        region.on("update-end", handleRegionUpdate);
      }
    }
  }, [waveSurfer, regionsPlugin, showLoopMarkers, duration, currentRegion]);

  // Old marker functions removed - now handled by WaveSurfer regions

  // Loop nudge control functions
  const NUDGE_STEP = 0.1; // Base step in seconds

  const expandLoopLeft = useCallback(
    (multiplier = 1) => {
      const region = currentRegionRef.current;
      if (!region || !waveSurfer) return;
      const step = NUDGE_STEP * multiplier;
      const newStart = Math.max(0, region.start - step);
      region.setOptions?.({ start: newStart });
      setLoopStart(newStart);
      console.log("⬅️ Expanded loop left:", { newStart, end: region.end });
    },
    [waveSurfer],
  );

  const expandLoopRight = useCallback(
    (multiplier = 1) => {
      const region = currentRegionRef.current;
      if (!region || !waveSurfer) return;
      const step = NUDGE_STEP * multiplier;
      const maxEnd = waveSurfer.getDuration();
      const newEnd = Math.min(maxEnd, region.end + step);
      region.setOptions?.({ end: newEnd });
      setLoopEnd(newEnd);
      console.log("➡️ Expanded loop right:", { start: region.start, newEnd });
    },
    [waveSurfer],
  );

  const contractLoopLeft = useCallback(
    (multiplier = 1) => {
      const region = currentRegionRef.current;
      if (!region || !waveSurfer) return;
      const step = NUDGE_STEP * multiplier;
      const newStart = region.start + step;
      if (newStart < region.end) {
        region.setOptions?.({ start: newStart });
        setLoopStart(newStart);
        console.log("➡️⬅️ Contracted loop from left:", {
          newStart,
          end: region.end,
        });
      }
    },
    [waveSurfer],
  );

  const contractLoopRight = useCallback(
    (multiplier = 1) => {
      const region = currentRegionRef.current;
      if (!region || !waveSurfer) return;
      const step = NUDGE_STEP * multiplier;
      const newEnd = region.end - step;
      if (newEnd > region.start) {
        region.setOptions?.({ end: newEnd });
        setLoopEnd(newEnd);
        console.log("➡️⬅️ Contracted loop from right:", {
          start: region.start,
          newEnd,
        });
      }
    },
    [waveSurfer],
  );

  // Notify parent when loop state changes
  useEffect(() => {
    if (onLoopStateChange) {
      onLoopStateChange(showLoopMarkers);
    }
  }, [showLoopMarkers, onLoopStateChange]);

  // Provide loop controls to parent when they become available
  useEffect(() => {
    if (onLoopControlsReady && showLoopMarkers && currentRegion) {
      onLoopControlsReady({
        expandLeft: expandLoopLeft,
        expandRight: expandLoopRight,
        contractLeft: contractLoopLeft,
        contractRight: contractLoopRight,
      });
    } else if (onLoopControlsReady && !showLoopMarkers) {
      onLoopControlsReady(null);
    }
  }, [
    showLoopMarkers,
    currentRegion,
    onLoopControlsReady,
    expandLoopLeft,
    expandLoopRight,
    contractLoopLeft,
    contractLoopRight,
  ]);

  // Format time display
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  if (!audioUrl) {
    return null;
  }

  if (error) {
    return (
      <div className="p-3 rounded border border-ember-500 bg-ember-900/20 text-ember-300">
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div
      className={`audio-player ${compact ? "p-2" : "p-4"} ${compact ? "" : "rounded-lg border border-forest-600 bg-forest-800"}`}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Audio info */}
      {!compact && audioFilename && (
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium truncate text-gold-500">
                {audioFilename}
              </h4>
              {audioSize && (
                <p className="text-xs mt-1 text-gold-400">
                  {audioStorageService.formatFileSize(audioSize)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Player controls */}
      {compact ? (
        /* Compact horizontal layout: [Play] [Seek Bar] [Volume] [Time Display] */
        <div className="relative flex items-center gap-2 w-full">
          {/* Play/pause button */}
          <button
            onClick={togglePlayPause}
            disabled={isLoading || waveformLoading}
            className={`flex items-center justify-center w-8 h-8 rounded transition-colors flex-shrink-0 ${
              isLoading
                ? "bg-forest-700 text-forest-500"
                : "bg-forest-700 hover:bg-forest-600 text-gold-500"
            }`}
          >
            {isLoading || waveformLoading ? (
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>

          {/* A-B Loop toggle button */}
          <button
            onClick={toggleLoopMarkers}
            className={`flex items-center justify-center w-8 h-8 rounded transition-colors flex-shrink-0 ${
              showLoopMarkers
                ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                : "bg-forest-700 hover:bg-forest-600 text-forest-300"
            }`}
            title={
              showLoopMarkers
                ? "Hide A-B loop markers"
                : "Show A-B loop markers"
            }
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {/* WaveSurfer container - flexible width */}
          <div
            className="mx-2 relative"
            style={{
              flex: 1,
              minWidth: "100px",
            }}
          >
            {/* Loading state for waveform */}
            {waveformLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center rounded-full border z-10 border-forest-600 bg-forest-800"
                style={{ height: "16px" }}
              >
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* WaveSurfer container for compact mode */}
            <div
              ref={waveformRef}
              className="waveform-container"
              style={{
                height: "16px",
                width: "100%",
                opacity: waveformLoading ? 0.3 : 1,
                backgroundColor: "#2e3e3a",
                border: "1px solid #4a6057",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            />
          </div>

          {/* Volume button with dropdown toggle - hidden on mobile notepad */}
          {!isMobile && (
            <div className="relative flex-shrink-0">
              <button
                onClick={toggleVolume}
                className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>

              {/* Volume slider dropdown - position below button, align to right */}
              {hideMenu && showVolumeSlider && (
                <div
                  ref={volumeSliderRef}
                  className="absolute top-full right-0 mt-1 p-2 rounded-lg border shadow-lg z-50 border-forest-600 bg-forest-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) =>
                      handleVolumeChange(parseFloat(e.target.value))
                    }
                    className="w-16 h-2 rounded-lg appearance-none cursor-pointer bg-forest-700"
                    style={{
                      accentColor: "#c5a34e",
                      background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(isMuted ? 0 : volume) * 100}%, #2e3e3a ${(isMuted ? 0 : volume) * 100}%, #2e3e3a 100%)`,
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Menu button - hide when hideMenu is true */}
          {!hideMenu &&
            showControls &&
            (onDownload || onRemove || onReplace) && (
              <div className="relative flex-shrink-0">
                <button
                  ref={menuButtonRef}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </div>
            )}

          {/* Time display in compact mode */}
          <div className="text-xs whitespace-nowrap flex-shrink-0 text-forest-300">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      ) : (
        /* Original vertical layout for non-compact mode */
        <div className="space-y-3">
          {/* WaveSurfer container */}
          <div className="space-y-1">
            {/* Loading state for waveform */}
            {waveformLoading && (
              <div
                className="relative flex items-center justify-center rounded-full border border-forest-600 bg-forest-800"
                style={{ height: "24px", width: "100%" }}
              >
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* WaveSurfer container for vertical mode */}
            <div
              ref={waveformRefVertical}
              className="waveform-container"
              style={{
                height: "24px",
                width: "100%",
                opacity: waveformLoading ? 0.3 : 1,
                backgroundColor: "#2e3e3a",
                border: "1px solid #4a6057",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            />

            {/* Time display */}
            <div className="flex justify-between text-xs text-forest-300">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Control buttons */}
          {isMobile ? (
            /* Mobile: single flat flex row so slider can fill space */
            <div className="flex items-center gap-2">
              {/* Play/pause — same size as volume button */}
              <button
                onClick={togglePlayPause}
                disabled={isLoading || waveformLoading}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  isLoading
                    ? "bg-forest-700 text-forest-500"
                    : "bg-forest-700 hover:bg-forest-600 text-gold-500"
                }`}
              >
                {isLoading || waveformLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-3.5 h-3.5" />
                ) : (
                  <Play className="w-3.5 h-3.5 ml-0.5" />
                )}
              </button>

              {/* Loop — same size and icon as record player */}
              <button
                onClick={toggleLoopMarkers}
                className={`flex-shrink-0 p-1 rounded transition-colors ${
                  showLoopMarkers
                    ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                    : "bg-forest-700 hover:bg-forest-600 text-forest-300"
                }`}
                title={
                  showLoopMarkers
                    ? "Hide A-B loop markers"
                    : "Show A-B loop markers"
                }
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>

              {/* Volume button */}
              <button
                onClick={toggleMute}
                className="flex-shrink-0 p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
              >
                {isMuted ? (
                  <VolumeX className="w-3.5 h-3.5" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5" />
                )}
              </button>

              {/* Volume slider — fills remaining space */}
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="h-2 rounded-lg appearance-none cursor-pointer bg-forest-700"
                style={{
                  flex: "1 1 0%",
                  minWidth: 0,
                  transform: "translateY(7px)",
                  accentColor: "#c5a34e",
                  background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(isMuted ? 0 : volume) * 100}%, #2e3e3a ${(isMuted ? 0 : volume) * 100}%, #2e3e3a 100%)`,
                }}
              />

              {/* Menu button */}
              {showControls && (onDownload || onRemove || onReplace) && (
                <button
                  ref={menuButtonRef}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="flex-shrink-0 p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            /* Desktop: two-group layout */
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Play/pause */}
                <button
                  onClick={togglePlayPause}
                  disabled={isLoading || waveformLoading}
                  className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                    isLoading
                      ? "bg-forest-700 text-forest-500"
                      : "bg-forest-700 hover:bg-forest-600 text-gold-500"
                  }`}
                >
                  {isLoading || waveformLoading ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </button>

                {/* Loop */}
                <button
                  onClick={toggleLoopMarkers}
                  className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                    showLoopMarkers
                      ? "bg-forest-600 text-gold-500 border border-gold-500/30"
                      : "bg-forest-700 hover:bg-forest-600 text-forest-300"
                  }`}
                  title={
                    showLoopMarkers
                      ? "Hide A-B loop markers"
                      : "Show A-B loop markers"
                  }
                >
                  <RefreshCw className="w-5 h-5" />
                </button>

                {/* Loop nudge controls — inline after loop button (desktop) */}
                {showLoopMarkers && (
                  <LoopNudgeControls
                    inline
                    onExpandLeft={expandLoopLeft}
                    onExpandRight={expandLoopRight}
                    onContractLeft={contractLoopLeft}
                    onContractRight={contractLoopRight}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMute}
                  className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={(e) =>
                    handleVolumeChange(parseFloat(e.target.value))
                  }
                  className="w-32 h-2 rounded-lg appearance-none cursor-pointer bg-forest-700"
                  style={{
                    accentColor: "#c5a34e",
                    background: `linear-gradient(to right, #c5a34e 0%, #c5a34e ${(isMuted ? 0 : volume) * 100}%, #2e3e3a ${(isMuted ? 0 : volume) * 100}%, #2e3e3a 100%)`,
                  }}
                />

                {/* Menu button */}
                {showControls && (onDownload || onRemove || onReplace) && (
                  <div className="relative ml-2">
                    <button
                      ref={menuButtonRef}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowMenu(!showMenu);
                      }}
                      className="p-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-gold-500"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shared portal menu for download/replace/remove */}
      {showMenu &&
        (onDownload || onRemove || onReplace) &&
        (() => {
          const menuItems = [];
          if (onDownload) {
            menuItems.push({
              key: "download",
              label: "Download",
              icon: Download,
              onClick: () => {
                handleDownload();
                setShowMenu(false);
              },
              danger: false,
            });
          }
          if (onReplace) {
            menuItems.push({
              key: "replace",
              label: "Replace",
              icon: RotateCcw,
              onClick: () => {
                onReplace();
                setShowMenu(false);
              },
              danger: false,
            });
          }
          if (onRemove) {
            menuItems.push({
              key: "remove",
              label: "Remove",
              icon: Trash2,
              onClick: () => {
                onRemove();
                setShowMenu(false);
              },
              danger: true,
            });
          }

          return createPortal(
            isMobile ? (
              /* Mobile: backdrop + slide-up panel (matches ExportDropdown) */
              <>
                {/* Backdrop */}
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 99998,
                    background: "rgba(0,0,0,0.3)",
                  }}
                  onClick={() => setShowMenu(false)}
                />
                {/* Slide-up panel above navbar */}
                <div
                  ref={dropdownRef}
                  style={{
                    position: "fixed",
                    bottom:
                      "max(84px, calc(68px + env(safe-area-inset-bottom, 0px)))",
                    left: 0,
                    right: 0,
                    zIndex: 99999,
                    animation: "audioMenuSlideUp 0.2s ease-out",
                  }}
                >
                  <div
                    className="bg-forest-800 border-t border-forest-700"
                    style={{ display: "block", padding: "6px 8px" }}
                  >
                    {menuItems.map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.key}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            item.onClick();
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "100%",
                            fontSize: "14px",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            marginBottom:
                              idx < menuItems.length - 1 ? "4px" : "0",
                          }}
                          className={`transition-colors ${
                            item.danger
                              ? "bg-ember-900/30 hover:bg-ember-900/50 text-ember-300"
                              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                          }`}
                        >
                          <Icon
                            style={{
                              width: "20px",
                              height: "20px",
                              flexShrink: 0,
                            }}
                            className={
                              item.danger ? "text-ember-400" : "text-gold-500"
                            }
                          />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <style>{`
                  @keyframes audioMenuSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                  }
                `}</style>
              </>
            ) : (
              /* Desktop: fixed dropdown below button (matches ExportDropdown) */
              <div
                ref={dropdownRef}
                style={{
                  position: "fixed",
                  zIndex: 99999,
                  top: menuButtonRef.current
                    ? `${menuButtonRef.current.getBoundingClientRect().bottom + 4}px`
                    : "auto",
                  right: menuButtonRef.current
                    ? `${window.innerWidth - menuButtonRef.current.getBoundingClientRect().right}px`
                    : "auto",
                }}
              >
                <div
                  className="bg-forest-800 border border-forest-700 rounded-lg shadow-xl"
                  style={{ display: "block", padding: "4px" }}
                >
                  {menuItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          width: "100%",
                          textAlign: "left",
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          marginBottom:
                            idx < menuItems.length - 1 ? "2px" : "0",
                        }}
                        className={`transition-colors ${
                          item.danger
                            ? "bg-ember-900/30 hover:bg-ember-900/50 text-ember-300"
                            : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                        }`}
                      >
                        <Icon
                          style={{
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                          }}
                          className={
                            item.danger ? "text-ember-400" : "text-gold-500"
                          }
                        />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ),
            document.body,
          );
        })()}

      {/* Marker tooltip */}
      {markerTooltip && (
        <div
          className="fixed px-2 py-1 text-xs rounded shadow-lg z-50 pointer-events-none"
          style={{
            left: markerTooltip.x,
            top: -30,
            backgroundColor: "#1f2f2a",
            color: "#ffffff",
            border: "1px solid #4a6057",
            transform: "translateX(-50%)",
          }}
        >
          {markerTooltip.type === "start" ? "A: " : "B: "}
          {formatTime(markerTooltip.time)}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
